import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight, Play, X } from 'lucide-react'
import Parallax from './Parallax'
import Reveal from './layout/Reveal'

type Film = {
  id: string
  title: string
  location: string
  duration: string
  poster: string
  url: string
  vimeoId?: string
  source?: string
  credit?: string
}

const FILMS: readonly Film[] = [
  {
    id: '1027985698',
    vimeoId: '1027985698',
    title: '40 Beach Avenue',
    location: 'Indian Beach · Rehoboth Beach',
    duration: '1:11',
    poster: '/media/hero/40-beach-avenue.jpg',
    url: 'https://vimeo.com/1027985698',
  },
  {
    id: '1011757950',
    vimeoId: '1011757950',
    title: '54 Eleanor Lee',
    location: 'Canal Corkran · Rehoboth Beach',
    duration: '1:20',
    poster: '/media/hero/54-eleanor-lee.jpg',
    url: 'https://vimeo.com/1011757950',
  },
  {
    id: '1193076094',
    vimeoId: '1193076094',
    title: '3 Persimmon Lane',
    location: 'Waterfront · Harbeson',
    duration: '0:56',
    poster: '/media/hero/3-persimmon-lane.jpg',
    url: 'https://vimeo.com/1193076094',
  },
  {
    id: 'coastal-delaware-drone-film',
    title: 'Coastal Delaware from Above',
    location: 'Lewes · Delaware Coast',
    duration: '7:09',
    credit: 'Elliot MacGuire Photography',
    poster: '/media/hero/drone-clips-poster.jpg',
    url: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-full.mp4',
    source: 'https://pub-fabb5cd90faf4ea58da21e10c156308d.r2.dev/coastal-delaware/drone-clips-full.mp4',
  },
]

const PropertyFilms = () => {
  const [activeFilmId, setActiveFilmId] = useState<string | null>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)
  const activeFilm = FILMS.find((film) => film.id === activeFilmId)

  useEffect(() => {
    if (!activeFilm) return
    const previousOverflow = document.body.style.overflow
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveFilmId(null)
        return
      }
      if (event.key !== 'Tab') return
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button, a[href], iframe') ?? [])
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    closeButtonRef.current?.focus()
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      triggerRef.current?.focus()
    }
  }, [activeFilm])

  const openFilm = (id: string) => {
    triggerRef.current = document.activeElement as HTMLElement | null
    setActiveFilmId(id)
  }

  const closeFilm = () => setActiveFilmId(null)

  const renderFilm = (film: Film, index: number, feature = false) => (
    <article className="group/film relative h-full overflow-hidden bg-primary-ink" data-film-number={`0${index + 1}`}>
      <div className={`relative isolate overflow-hidden ${feature ? 'min-h-[500px] md:min-h-[720px]' : 'min-h-[340px] lg:min-h-0 lg:h-full'}`}>
        <Parallax factor={feature ? 0.035 : 0.025} max={feature ? 32 : 20} className="absolute -inset-y-[6%] inset-x-0 -z-10">
          <img src={film.poster} alt="" width={1280} height={720} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover/film:scale-[1.045] motion-reduce:transition-none motion-reduce:group-hover/film:scale-100" />
        </Parallax>
        <button type="button" onClick={() => openFilm(film.id)} aria-label={`Play ${film.title}${film.vimeoId ? ' with sound' : ''}`} className="absolute inset-0 z-10 h-full w-full overflow-hidden text-left">
          <span className="absolute inset-0 bg-gradient-to-t from-primary-ink/95 via-primary-ink/10 to-primary-ink/25 transition-colors duration-700 group-hover/film:via-primary-ink/20" />
          <span className="absolute inset-4 border border-white/0 transition-all duration-700 group-hover/film:inset-5 group-hover/film:border-white/30" aria-hidden="true" />
          <span className="absolute -right-3 -top-8 font-serif text-[9rem] italic leading-none text-white/[0.08] transition-all duration-[1200ms] group-hover/film:-translate-x-5 group-hover/film:text-white/[0.16] md:text-[12rem]" aria-hidden="true">0{index + 1}</span>
          <span className="absolute left-5 top-5 flex items-center gap-3 font-sans text-[8px] font-medium uppercase tracking-[0.22em] text-white/70 md:left-8 md:top-8">
            <span className="h-px w-8 bg-accent-gold transition-all duration-700 group-hover/film:w-14" aria-hidden="true" /> {film.credit ? `Drone film · ${film.credit}` : `Property film · 0${index + 1}`}
          </span>
          <span className={`absolute flex items-center justify-center text-white transition-all duration-700 group-hover/film:scale-110 group-hover/film:text-primary-ink ${feature ? 'right-7 top-7 h-20 w-20 md:right-12 md:top-12 md:h-24 md:w-24' : 'right-6 top-6 h-16 w-16'}`}>
            <span className="coastal-orbit absolute inset-0 rounded-full border border-white/55 group-hover/film:border-accent-gold group-hover/film:bg-accent-gold motion-reduce:animate-none" aria-hidden="true" />
            <span className="absolute inset-2 rounded-full border border-white/25 transition-transform duration-700 group-hover/film:scale-75" aria-hidden="true" />
            <Play size={feature ? 25 : 20} fill="currentColor" className="relative" aria-hidden="true" />
          </span>
          <span className={`absolute bottom-0 left-0 right-0 text-white ${feature ? 'p-8 md:p-12' : 'p-7'}`}>
            <span className="block font-sans text-[9px] font-medium uppercase tracking-[0.24em] text-white/65">{film.location} · {film.duration}</span>
            <span className={`mt-3 block max-w-[85%] font-serif font-normal leading-none tracking-[-0.04em] transition-transform duration-700 group-hover/film:-translate-y-2 ${feature ? 'text-5xl md:text-8xl' : 'text-4xl md:text-5xl'}`}>{film.title}</span>
            <span className="mt-5 inline-flex translate-y-3 items-center gap-3 font-sans text-[8px] font-medium uppercase tracking-[0.18em] text-white/0 transition-all duration-700 group-hover/film:translate-y-0 group-hover/film:text-white/75">{film.vimeoId ? 'Watch with sound' : 'Watch full film'} <ArrowUpRight size={13} aria-hidden="true" /></span>
          </span>
          <span className="absolute bottom-0 left-0 h-1 w-full origin-left scale-x-0 bg-accent-gold transition-transform duration-[1000ms] group-hover/film:scale-x-100" aria-hidden="true" />
        </button>
      </div>
    </article>
  )

  return (
    <>
      <section id="home-tours" className="relative overflow-hidden bg-primary-mist py-24 text-primary-ink md:py-36">
        <div className="coastal-orbit pointer-events-none absolute -left-56 top-1/4 h-[40rem] w-[40rem] rounded-full border border-primary-navy/15 motion-reduce:animate-none" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 top-1/4 overflow-hidden text-center font-serif text-[clamp(6rem,18vw,16rem)] italic leading-none tracking-[-0.08em] text-primary-ink/[0.025]" aria-hidden="true">FILM</div>
        <p className="absolute right-5 top-1/2 hidden [writing-mode:vertical-rl] font-sans text-[8px] font-medium uppercase tracking-[0.3em] text-primary-ink/40 xl:block">Atlantic Exposure · Coastal Delaware · Property cinema</p>

        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-[0.68fr_0.32fr] lg:items-end lg:gap-16">
              <div>
                <div className="flex items-center gap-4 text-primary-ink"><span className="h-px w-10 bg-accent-gold" aria-hidden="true" /><p className="font-sans text-[10px] font-medium uppercase tracking-[0.28em]">Cinematic property marketing</p></div>
                <Parallax axis="x" factor={0.045} max={28}>
                  <h2 className="mt-7 max-w-4xl font-serif text-[clamp(4rem,8vw,8.5rem)] font-normal leading-[0.84] tracking-[-0.06em]">Every home deserves<br /><span className="italic text-primary-navy">a point of view.</span></h2>
                </Parallax>
              </div>
              <div className="border-l border-primary-ink/20 pl-7 lg:pb-2">
                <p className="max-w-md text-base font-light leading-[1.9] text-primary-ink/70">Merritt &amp; Shawn use cinematic storytelling to make setting, craftsmanship, and coastal lifestyle impossible to miss.</p>
                <div className="mt-7 grid grid-cols-3 border-y border-primary-ink/15 py-4 font-sans text-[8px] font-medium uppercase tracking-[0.15em] text-primary-ink/55">
                  <span><strong className="block font-serif text-2xl font-normal text-primary-ink">04</strong>Films</span>
                  <span className="border-l border-primary-ink/15 pl-4"><strong className="block font-serif text-2xl font-normal text-primary-ink">04</strong>Settings</span>
                  <span className="border-l border-primary-ink/15 pl-4"><strong className="block font-serif text-2xl font-normal text-primary-ink">01</strong>Standard</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="relative mx-auto mt-20 max-w-[1500px] px-0 md:px-6">
          <Reveal>
            <div className="grid gap-px bg-primary-mist shadow-[0_35px_90px_rgba(22,50,71,0.18)] lg:grid-cols-[1.55fr_0.75fr] lg:grid-rows-3" data-film-strip>
              <div data-film-feature className="lg:row-span-3">{renderFilm(FILMS[0], 0, true)}</div>
              {FILMS.slice(1).map((film, index) => <div key={film.id}>{renderFilm(film, index + 1)}</div>)}
            </div>
          </Reveal>
        </div>

        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="mt-12 grid gap-6 border-y border-primary-ink/20 py-7 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <p className="font-serif text-2xl italic text-primary-ink md:text-3xl">Deep roots. Builder&apos;s eye. A campaign built to move.</p>
                <p className="mt-2 font-sans text-[9px] font-medium uppercase tracking-[0.16em] text-primary-ink/55">The Carrie Lingo Team · Jack Lingo REALTOR® · trusted at the Delaware beaches since 1974</p>
              </div>
              <a href={FILMS[0].url} target="_blank" rel="noopener noreferrer" className="group inline-flex min-h-12 items-center gap-4 bg-primary-ink px-6 font-sans text-[9px] font-medium uppercase tracking-[0.18em] text-white transition-all hover:-translate-y-1 hover:bg-accent-gold hover:text-primary-ink motion-reduce:transition-none">View the film archive <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" aria-hidden="true" /></a>
            </div>
          </Reveal>
        </div>
      </section>

      {activeFilm && (
        <div
          ref={dialogRef}
          data-film-lightbox
          role="dialog"
          aria-modal="true"
          aria-labelledby="active-film-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeFilm()
          }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-primary-ink/95 p-4 backdrop-blur-md md:p-8"
        >
          <div className="w-full max-w-6xl">
            <div className="mb-4 flex items-end justify-between gap-6 text-white">
              <div>
                <p className="font-sans text-[9px] font-medium uppercase tracking-[0.24em] text-primary-mist">{activeFilm.location} · {activeFilm.duration}{activeFilm.credit ? ` · Film by ${activeFilm.credit}` : ''}</p>
                <h3 id="active-film-title" className="mt-2 font-serif text-3xl leading-none md:text-5xl">{activeFilm.title}</h3>
              </div>
              <button ref={closeButtonRef} type="button" onClick={closeFilm} aria-label={`Close ${activeFilm.title} video`} className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/40 text-white transition-colors hover:border-accent-gold hover:bg-accent-gold hover:text-primary-ink">
                <X size={19} aria-hidden="true" />
              </button>
            </div>
            <div className="relative aspect-video w-full overflow-hidden bg-black shadow-[0_36px_100px_rgba(0,0,0,0.5)]">
              {activeFilm.vimeoId ? (
                <iframe
                  src={`https://player.vimeo.com/video/${activeFilm.vimeoId}?autoplay=1&muted=0&controls=1&title=0&byline=0&portrait=0&dnt=1&playsinline=1`}
                  title={`${activeFilm.title} property tour`}
                  allow="autoplay; fullscreen; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full border-0"
                />
              ) : (
                <video src={activeFilm.source} poster={activeFilm.poster} controls autoPlay playsInline preload="metadata" className="absolute inset-0 h-full w-full object-contain" />
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <a href={activeFilm.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-3 font-sans text-[9px] font-medium uppercase tracking-[0.18em] text-white/70 transition-colors hover:text-accent-gold">{activeFilm.vimeoId ? 'Watch on Vimeo' : 'Open full film'} <ArrowUpRight size={14} aria-hidden="true" /></a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PropertyFilms
